import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { NewProductForm } from '../types/product';
import ImageUploader from '../components/utils/ImageUploader';
import RichTextEditor from './utils/RichTextEditor';
import { AlertCircle } from 'lucide-react';
import VariantsEditor from './VariantsEditor';
import { newVariant, validateVariants } from '../lib/variants';

interface ProductFormProps {
  onSubmit: (product: NewProductForm) => Promise<boolean>;
  onCancel: () => void;
  initialData?: NewProductForm;
}

// Un miele nuovo parte con i tre formati del listino, da compilare.
const initialFormState: NewProductForm = {
  name: '',
  description: '',
  long_description: '',
  image_url: '',
  category: '',
  variants: [newVariant('250 g', 250, 1), newVariant('500 g', 500, 2), newVariant('1 kg', 1000, 3)],
};

const ProductForm: React.FC<ProductFormProps> = ({ onSubmit, onCancel, initialData = initialFormState }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [values, setValues] = useState<NewProductForm>(initialData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const setFieldValue = <K extends keyof NewProductForm>(field: K, value: NewProductForm[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = (data: NewProductForm) => {
    const errors: Record<string, string> = {};
    if (!data.name || data.name.trim().length === 0) errors.name = 'Il nome è obbligatorio';
    const variantsError = validateVariants(data.variants);
    if (variantsError) errors.variants = variantsError;
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validate(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    const success = await onSubmit(values);
    setIsSubmitting(false);

    if (success) {
      setValues(initialFormState);
      setUploadError(null);
    }
  };

  const handleImageUpload = (imageUrl: string) => {
    setFieldValue('image_url', imageUrl);
    setUploadError(null);
  };

  const handleUploadError = (error: string) => {
    setUploadError(error);
  };

  return (
    <div className="rounded-xl bg-card p-6 shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
      <h2 className="mb-6 font-display text-xl font-semibold text-foreground">
        Aggiungi un Nuovo Prodotto
      </h2>

      {uploadError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nome Prodotto */}
          <div className="space-y-1">
            <label htmlFor="name" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Nome Prodotto *
            </label>
            <Input
              id="name"
              placeholder="Inserisci il nome del prodotto"
              required
              value={values.name}
              onChange={(e) => setFieldValue('name', e.target.value)}
              className={fieldErrors.name ? 'border-destructive' : undefined}
            />
            {fieldErrors.name && <p className="mt-1 text-xs font-semibold text-danger">{fieldErrors.name}</p>}
          </div>

          {/* Categoria */}
          <div className="space-y-1">
            <label htmlFor="category" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Categoria
            </label>
            <Input
              id="category"
              placeholder="ESEMPIO"
              value={values.category?.toUpperCase() || ''}
              onChange={(e) => setFieldValue('category', e.target.value.toUpperCase())}
            />
          </div>

          {/* Formati */}
          <div className="md:col-span-2 space-y-1">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Formati e prezzi *
            </span>
            <VariantsEditor
              variants={values.variants}
              onChange={(variants) => setFieldValue('variants', variants)}
              error={fieldErrors.variants}
            />
          </div>

          {/* Descrizione Breve */}
          <div className="md:col-span-2 space-y-1">
            <label htmlFor="description" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Descrizione Breve *
            </label>
            <Textarea
              id="description"
              placeholder="Inserisci una descrizione breve del prodotto (max 255 caratteri)"
              required
              rows={3}
              maxLength={255}
              value={values.description || ''}
              onChange={(e) => setFieldValue('description', e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {values.description?.length || 0}/255 caratteri
            </p>
          </div>

          {/* Descrizione Estesa */}
          <div className="md:col-span-2 space-y-1">
            <label htmlFor="long_description" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Descrizione Estesa (HTML)
            </label>
            <RichTextEditor
              value={values.long_description || ''}
              onChange={(value) => setFieldValue('long_description', value)}
              placeholder="Inserisci la descrizione completa in formato HTML..."
            />
          </div>

          {/* Image Uploader */}
          <div className="md:col-span-2">
            <ImageUploader
              onImageUpload={handleImageUpload}
              onUploadError={handleUploadError}
              currentImage={values.image_url}
              disabled={isSubmitting}
            />
          </div>

          {/* Actions */}
          <div className="md:col-span-2">
            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="h-10 rounded-lg bg-card px-4 text-[13px] font-bold text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:bg-muted"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {isSubmitting ? 'Aggiungo…' : 'Aggiungi prodotto'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
